/**
 * Report Viewer Modal Component
 * Displays the full content of a historical trading report
 */

import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import {
  XMarkIcon,
  ChartBarIcon,
  TrophyIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import type { ReportData } from '../../services/tradingReportsApi';

// Format report period - use the human-friendly period if available
const formatReportPeriod = (reportData: ReportData): string => {
  // First, try to use the human_friendly_period from the report data
  if (reportData.human_friendly_period) {
    return reportData.human_friendly_period;
  }
  
  // Fallback to custom formatting if available
  if (!reportData.period_start || !reportData.period_end) {
    return 'Unknown period';
  }
  
  const start = new Date(reportData.period_start);
  const end = new Date(reportData.period_end);
  
  // Validate dates
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 'Invalid date range';
  }
  
  // Format for display
  const formatDateTime = (date: Date): string => {
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const hours = date.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${day} ${month} ${year} ${displayHours} ${ampm}`;
  };
  
  // For daily reports, show the end time with a note that it's the last 24 hours
  if (reportData.report_type === 'daily') {
    const day = end.getDate();
    const month = end.toLocaleDateString('en-US', { month: 'short' });
    const year = end.getFullYear();
    const hours = end.getHours();
    const minutes = end.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : '';
    
    return `${day} ${month} ${year} ${displayHours}${displayMinutes} ${ampm} (Last 24 hours prior)`;
  }
  
  // Otherwise show full range with times
  return `${formatDateTime(start)} to ${formatDateTime(end)}`;
};

interface ReportViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportData: ReportData | null;
}

export const ReportViewerModal: React.FC<ReportViewerModalProps> = ({
  isOpen,
  onClose,
  reportData,
}) => {
  if (!reportData) return null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-blue-900/30 to-purple-900/30 border border-white/20 p-6 shadow-2xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/20">
                  <Dialog.Title className="flex items-center space-x-3">
                    <ChartBarIcon className="w-8 h-8 text-blue-400" />
                    <div>
                      <h3 className="text-2xl font-bold text-white">
                        {reportData.report_type.charAt(0).toUpperCase() + reportData.report_type.slice(1)} Report
                      </h3>
                      <p className="text-sm text-white/60 mt-1">
                        {formatReportPeriod(reportData)}
                      </p>
                    </div>
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6 text-white/70 hover:text-white" />
                  </button>
                </div>

                {/* Content */}
                <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
                  {!reportData.has_activity ? (
                    <div className="text-center py-12 bg-white/5 rounded-lg border border-white/10">
                      <ChartBarIcon className="w-16 h-16 text-white/30 mx-auto mb-4" />
                      <p className="text-white/70 text-lg">No Trading Activity</p>
                      <p className="text-sm text-white/50 mt-2">
                        There were no trades during this period
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Summary Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-white/70">Total P&L</span>
                            <CurrencyDollarIcon className="w-5 h-5 text-white/50" />
                          </div>
                          <p className={`text-2xl font-bold ${reportData.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {formatCurrency(reportData.total_pnl)}
                          </p>
                          <p className={`text-sm ${reportData.total_pnl_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {formatPercent(reportData.total_pnl_pct)}
                          </p>
                        </motion.div>

                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 }}
                          className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-white/70">Win Rate</span>
                            <TrophyIcon className="w-5 h-5 text-white/50" />
                          </div>
                          <p className="text-2xl font-bold text-white">
                            {reportData.win_rate.toFixed(1)}%
                          </p>
                          <p className="text-sm text-white/60">
                            {reportData.wins}W / {reportData.losses}L
                          </p>
                        </motion.div>

                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                          className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-white/70">Trades Closed</span>
                            <ArrowTrendingDownIcon className="w-5 h-5 text-white/50" />
                          </div>
                          <p className="text-2xl font-bold text-white">
                            {reportData.trades_closed}
                          </p>
                          <p className="text-sm text-white/60">Completed</p>
                        </motion.div>

                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                          className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-white/70">Trades Opened</span>
                            <ArrowTrendingUpIcon className="w-5 h-5 text-white/50" />
                          </div>
                          <p className="text-2xl font-bold text-white">
                            {reportData.trades_opened}
                          </p>
                          <p className="text-sm text-white/60">New Positions</p>
                        </motion.div>
                      </div>

                      {/* Top Strategies */}
                      {reportData.top_strategies && reportData.top_strategies.length > 0 && (
                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4">
                          <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                            <TrophyIcon className="w-5 h-5 mr-2 text-yellow-400" />
                            Top Performing Strategies
                          </h4>
                          <div className="space-y-3">
                            {reportData.top_strategies.map((strategy, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                              >
                                <div className="flex-1">
                                  <p className="font-medium text-white">{strategy.name}</p>
                                  <p className="text-xs text-white/60">
                                    {strategy.trade_count} {strategy.trade_count === 1 ? 'trade' : 'trades'}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className={`font-bold ${strategy.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {formatCurrency(strategy.pnl)}
                                  </p>
                                  <p className={`text-sm ${strategy.pnl_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {formatPercent(strategy.pnl_pct)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Account Performance */}
                      {reportData.accounts && reportData.accounts.length > 0 && (
                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4">
                          <h4 className="text-lg font-semibold text-white mb-4">Account Performance</h4>
                          <div className="space-y-3">
                            {reportData.accounts.map((account, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                              >
                                <div className="flex-1">
                                  <p className="font-medium text-white">{account.account}</p>
                                  <p className="text-xs text-white/60">
                                    {account.trade_count} trades • {account.win_rate.toFixed(0)}% win rate
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className={`font-bold ${account.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {formatCurrency(account.pnl)}
                                  </p>
                                  <p className={`text-sm ${account.pnl_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {formatPercent(account.pnl_pct)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Closed Trades */}
                      {reportData.closed_trades && reportData.closed_trades.length > 0 && (
                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4">
                          <h4 className="text-lg font-semibold text-white mb-4">Closed Trades</h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-white/10">
                                  <th className="text-left text-white/70 font-medium pb-2">Symbol</th>
                                  <th className="text-left text-white/70 font-medium pb-2">Side</th>
                                  <th className="text-left text-white/70 font-medium pb-2">Strategy</th>
                                  <th className="text-left text-white/70 font-medium pb-2">Account</th>
                                  <th className="text-right text-white/70 font-medium pb-2">P&L</th>
                                  <th className="text-right text-white/70 font-medium pb-2">Closed At</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reportData.closed_trades.map((trade, idx) => (
                                  <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                                    <td className="py-2 text-white font-medium">{trade.symbol}</td>
                                    <td className="py-2">
                                      <span className={`px-2 py-0.5 rounded text-xs ${
                                        trade.side === 'Buy' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                                      }`}>
                                        {trade.side}
                                      </span>
                                    </td>
                                    <td className="py-2 text-white/70 text-xs">{trade.strategy}</td>
                                    <td className="py-2 text-white/70 text-xs">{trade.account}</td>
                                    <td className={`py-2 text-right font-medium ${trade.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                      {formatCurrency(trade.pnl)}
                                    </td>
                                    <td className="py-2 text-right text-white/60 text-xs">{trade.closed_at}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Open Positions */}
                      {reportData.has_open_positions && reportData.open_positions && reportData.open_positions.length > 0 && (
                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4">
                          <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                            <ClockIcon className="w-5 h-5 mr-2 text-blue-400" />
                            Open Positions ({reportData.open_positions_count})
                          </h4>
                          <p className="text-sm text-white/60 mb-4">
                            Total Unrealized P&L: <span className={`font-bold ${reportData.open_positions_total_unrealized_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {formatCurrency(reportData.open_positions_total_unrealized_pnl)}
                            </span>
                          </p>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-white/10">
                                  <th className="text-left text-white/70 font-medium pb-2">Symbol</th>
                                  <th className="text-left text-white/70 font-medium pb-2">Side</th>
                                  <th className="text-right text-white/70 font-medium pb-2">Size</th>
                                  <th className="text-right text-white/70 font-medium pb-2">Entry</th>
                                  <th className="text-right text-white/70 font-medium pb-2">Mark</th>
                                  <th className="text-right text-white/70 font-medium pb-2">Unrealized P&L</th>
                                  <th className="text-left text-white/70 font-medium pb-2">Account</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reportData.open_positions.map((position, idx) => (
                                  <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                                    <td className="py-2 text-white font-medium">{position.symbol}</td>
                                    <td className="py-2">
                                      <span className={`px-2 py-0.5 rounded text-xs ${
                                        position.side === 'Buy' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                                      }`}>
                                        {position.side}
                                      </span>
                                    </td>
                                    <td className="py-2 text-right text-white/70">{position.size}</td>
                                    <td className="py-2 text-right text-white/70">${position.avgPrice.toFixed(4)}</td>
                                    <td className="py-2 text-right text-white/70">${position.markPrice.toFixed(4)}</td>
                                    <td className={`py-2 text-right font-medium ${position.unrealisedPnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                      {formatCurrency(position.unrealisedPnl)}
                                    </td>
                                    <td className="py-2 text-white/70 text-xs">{position.accountname}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Footer */}
                <div className="mt-6 pt-4 border-t border-white/20 flex justify-end">
                  <button
                    onClick={onClose}
                    className="px-6 py-2 text-sm rounded-md font-semibold text-white bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400/50 transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
